import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getEmployees, deleteEmployee } from "../../services/employeeService";
import { Table, Button, Container, Spinner, Modal, Form, FormControl } from "react-bootstrap";
import { FaThList, FaThLarge, FaTrashAlt, FaEdit } from "react-icons/fa";
import Layout from "../../components/Layout";
import TopBar from "../../components/TopBar";
import { stringToColor, getInitials } from "../../utility";
import { getDepartments } from "../../services/departmentService";
import { baseURL, imageUrl } from "../../services/api";

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState("card");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [departments, setDepartments] = useState([]);

  // Define employee types with color codes
  const employeeTypes = [
    { value: '', label: 'All Types', color: 'secondary' },
    { value: 'Permanent', label: 'Permanent', color: 'success' },
    { value: 'Contract', label: 'Contract', color: 'primary' },
    { value: 'Intern', label: 'Intern', color: 'info' },
    { value: 'Trainee', label: 'Trainee', color: 'warning' },
    { value: 'Probation', label: 'Probation', color: 'danger' },
    { value: 'Freelancer', label: 'Freelancer', color: 'dark' },
    { value: 'Part Time', label: 'Part Time', color: 'secondary' },
  ];

  // Helper function to get type color
  const getTypeColor = (type) => {
    const foundType = employeeTypes.find(t => t.value === type);
    return foundType ? foundType.color : 'secondary';
  };
  const navigate = useNavigate();

  const loadEmployees = () => {
    setIsLoading(true);
    getEmployees()
      .then((res) => {
        setEmployees(res.data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching employees:", err);
        setIsLoading(false);
      });
  };

  const loadDepartments = () => {
    getDepartments()
      .then((departments) => {
        setDepartments(departments);
      })
      .catch((err) => {
        console.error("Error fetching departments:", err);
      });
  };

  useEffect(() => {
    loadEmployees();
    loadDepartments();
  }, []);

  const handleDelete = (id) => {
    setSelectedEmployeeId(id);
    setShowConfirmModal(true);
  };

  const confirmDelete = () => {
    if (!selectedEmployeeId) return;
    deleteEmployee(selectedEmployeeId)
      .then(() => {
        setShowConfirmModal(false);
        setSelectedEmployeeId(null);
        loadEmployees();
      })
      .catch((err) => {
        console.error("Error deleting employee:", err);
        setShowConfirmModal(false);
      });
  };

  const goToEdit = (id) => {
    navigate(`/employees/${id}`);
  };

  const goToCreate = () => {
    navigate("/employees/new");
  };

  const toggleView = () => {
    setViewMode(viewMode === "list" ? "card" : "list");
  };

  const filteredEmployees = employees.filter((emp) => {
    const name = emp.name?.toLowerCase() || '';
    const email = emp.email?.toLowerCase() || '';
    const searchValue = search.toLowerCase();
    const departmentId = emp.department_id;
    const empType = emp.employee_type || '';

    return (
      (name.includes(searchValue) || email.includes(searchValue)) &&
      (selectedDepartment === "" || departmentId === parseInt(selectedDepartment)) &&
      (selectedType === "" || empType === selectedType)
    );
  });

  return (
    <Layout>

      <TopBar >

        <Form className="d-none d-md-flex" style={{ width: '250px' }}>
          <FormControl
            type="search"
            placeholder="Search"
            aria-label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="me-2"
          />
        </Form>
      </TopBar>
      <Container fluid className="mt-4" style={{ overflowX: "auto" }}>
        {/* Mobile Search */}
        <div className="d-md-none mb-3">
          <FormControl
            type="search"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
          <div className="d-flex align-items-center mb-3 mb-md-0">
            <h3 className="main-title mb-0">
              Employee({filteredEmployees.length})
            </h3>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2">
            <div className="flex-grow-1 flex-md-grow-0">
              <Form.Select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                style={{ minWidth: '180px' }}
              >
                <option value="">All Departments</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="flex-grow-1 flex-md-grow-0">
              <Form.Select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                style={{ minWidth: '150px' }}
              >
                {employeeTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Form.Select>
            </div>
            <Button variant="outline-secondary" size="sm" onClick={toggleView}>
              {viewMode === "list" ? (
                <>
                  <FaThLarge className="me-1" /> Cards
                </>
              ) : (
                <>
                  <FaThList className="me-1" /> List
                </>
              )}
            </Button>
            <Button variant="primary" size="sm" onClick={goToCreate}>
              <i className="fas fa-plus me-1"></i>
              <span>Add</span>
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3">Loading employees...</p>
          </div>
        ) : viewMode === "list" ? (
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Profile</th>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Type</th>
                <th>PF Enabled</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center">No employees found.</td>
                </tr>
              ) : (
                filteredEmployees.map((emp, index) => (
                  <tr key={emp.id}>
                    <td>{index + 1}{console.log(emp.profile_image)}</td>
                    <td>
                      {emp.profile_image ? (
                        <img
                          src={`${imageUrl}${emp.profile_image}`}
                          alt="profile"
                          height="50"
                          width="50"
                          className="rounded-circle"
                        />
                      ) : (
                        <div
                          style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            backgroundColor: stringToColor(emp.name),
                            color: 'white',
                            fontSize: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {getInitials(emp.name)}
                        </div>
                      )}
                    </td>
                    <td>{emp.name}</td>
                    <td>{emp.email}</td>
                    <td>
                      <span className={`badge ${emp.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                        {emp.status}
                      </span>
                    </td>
                    <td>
                      <span className="badge bg-info text-dark">
                        {emp.employee_type || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${emp.pf_enabled ? 'bg-success' : 'bg-secondary'}`}>
                        {emp.pf_enabled ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>
                      <Button variant="info" size="sm" onClick={() => goToEdit(emp.id)}>
                        Edit
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => goToEdit(emp.id)} className="ms-2">
                        View
                      </Button>{" "}
                      <Button variant="danger" size="sm" onClick={() => handleDelete(emp.id)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        ) : (
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
            {filteredEmployees.length === 0 ? (
              <div className="col">
                <div className="text-center p-4">No employees found.</div>
              </div>
            ) : (
              filteredEmployees.map((emp) => (
                <div key={emp.id} className="col-md-4">
                  <div
                    className="card h-100 card-hover position-relative"
                    onClick={() => goToEdit(emp.id)}
                    style={{ cursor: "pointer" }}
                  >
                    {/* Delete icon at top-right */}
                    <div className="action-icons">

                      <div
                        className="delete-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(emp.id);
                        }}
                      >
                        <FaTrashAlt />
                      </div>
                    </div>

                    <div className="card-body">
                      <div className="d-flex align-items-center mb-3">
                        <div className="me-3">
                          {emp.profile_image ? (
                            <img
                              src={`${imageUrl}${emp.profile_image}`}
                              alt="profile"
                              className="rounded-circle"
                              width="60"
                              height="60"
                            />
                          ) : (
                            <div
                              style={{
                                width: '50px',
                                height: '50px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                backgroundColor: stringToColor(emp.name),
                                color: 'white'
                              }}
                            >
                              {getInitials(emp.name)}
                            </div>
                          )}
                        </div>
                        <div>
                          <h5 className="card-title mb-1">{emp.name}</h5>
                          <p className="card-text text-muted mb-1">{emp.email}</p>
                          <p className="card-text text-muted mb-1">{emp.phone}</p>
                          {emp.employee_type && (
                            <span className={`badge bg-${getTypeColor(emp.employee_type)}`}>
                              {emp.employee_type}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Deletion</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete this employee?
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
  );
};

export default EmployeeList;
